import { io, Socket } from "socket.io-client";

type TokenReader = () => string | null | undefined | Promise<string | null | undefined>;

/** Shared by web providers and the extension worker; never captures a token in the URL. */
export const createAuthedSocket = (url: string, readToken: TokenReader): Socket => {
	let disposed = false;
	let retryDelay = 1000;
	let retryTimer: ReturnType<typeof setTimeout> | undefined;
	const socket = io(url, {
		auth: async (callback) => {
			try {
				const token = await readToken();

				callback({ token });
			} catch {
				callback({ token: null });
			}
		},
		reconnection: true,
		reconnectionAttempts: Infinity,
		reconnectionDelay: 1000,
		reconnectionDelayMax: 10000,
		timeout: 20000,
		transports: ["websocket"],
	});
	const reconnect = async () => {
		try {
			const token = await readToken();

			if (token && !disposed) {
				socket.connect();
			}
		} catch {
			/* Storage unavailable: wait for a token change. */
		}
	};

	// Retry one server disconnect, including another device's logout. A refusal
	// destroys Socket.IO's subscription and does not trigger transport retries.
	socket.on("disconnect", (reason) => {
		if (reason === "io server disconnect") {
			return reconnect();
		}
	});
	socket.on("connect_error", (error) => {
		clearTimeout(retryTimer);
		if (error.message !== "SERVICE_UNAVAILABLE" || disposed) {
			return;
		}

		retryTimer = setTimeout(() => {
			void reconnect();
		}, retryDelay);
		retryDelay = Math.min(retryDelay * 2, 10000);
	});
	socket.on("connect", () => {
		clearTimeout(retryTimer);
		retryDelay = 1000;
	});
	// Teardown must cancel even a retry scheduled before the socket ever connected.
	const disconnect = socket.disconnect.bind(socket);

	socket.disconnect = () => {
		disposed = true;
		clearTimeout(retryTimer);

		return disconnect();
	};

	return socket;
};
